import {
  AlertTriangle,
  CheckCircle,
  ChevronDown,
  Clock,
  ExternalLink,
  Search,
  Shield,
  Smartphone,
  Star,
  XCircle,
} from 'lucide-react'
import { useState } from 'react'
import { SiGoogle } from 'react-icons/si'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import {
  googleSearchConsoleUrlInspection,
  type GoogleSearchConsoleUrlInspectionResult,
} from 'sprites-types/workflow/tools/googleSearchConsoleUrlInspection'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { SchemaValidationError } from '../shared/safeParseToolResult'

function GoogleSearchConsoleUrlInspectionDisplayComponent({
  nodeExecution,
}: WorkflowToolComponentProps) {
  const [isExpanded, setIsExpanded] = useState(true)
  const schema = getToolResultSchema(googleSearchConsoleUrlInspection, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const result = parseResult.data

  // Handle both array and object result formats
  const getResultData = (): GoogleSearchConsoleUrlInspectionResult => {
    if (Array.isArray(result)) {
      return result[0]
    }
    return result
  }

  const resultData = getResultData()

  const url = nodeExecution.inputs?.url || 'Unknown URL'
  const indexStatus = resultData?.indexStatus || 'UNKNOWN'
  const coverageState = resultData?.coverageState
  const lastCrawlTime = resultData?.lastCrawlTime
  const indexingState = resultData?.indexingState
  const issues = resultData?.issues || []
  const mobileUsability = resultData?.mobileUsability
  const richResults = resultData?.richResults

  const getIndexStatusStyles = (status: string) => {
    const statusStyles: Record<string, any> = {
      INDEXED: {
        bg: 'bg-gradient-to-br from-green-50 to-green-100/50',
        border: 'border-green-200',
        shadow: 'shadow-md shadow-green-100/50',
        text: 'text-green-700',
        iconBg: 'bg-green-100',
        iconText: 'text-green-600',
        icon: CheckCircle,
        badge: 'bg-green-100 text-green-700 border-green-200',
      },
      NOT_INDEXED: {
        bg: 'bg-gradient-to-br from-red-50 to-red-100/50',
        border: 'border-red-200',
        shadow: 'shadow-md shadow-red-100/50',
        text: 'text-red-700',
        iconBg: 'bg-red-100',
        iconText: 'text-red-600',
        icon: XCircle,
        badge: 'bg-red-100 text-red-700 border-red-200',
      },
    }
    return (
      statusStyles[status] || {
        bg: 'bg-gradient-to-br from-gray-50 to-gray-100/50',
        border: 'border-gray-200',
        shadow: 'shadow-md shadow-gray-100/50',
        text: 'text-gray-700',
        iconBg: 'bg-gray-100',
        iconText: 'text-gray-600',
        icon: AlertTriangle,
        badge: 'bg-gray-100 text-gray-700 border-gray-200',
      }
    )
  }

  const statusStyles = getIndexStatusStyles(indexStatus)
  const StatusIcon = statusStyles.icon

  const getVerdictStyles = (verdict: string) => {
    const verdictStyles: Record<string, any> = {
      PASS: {
        bg: 'bg-gradient-to-br from-green-50 to-green-100/50',
        border: 'border-green-200',
        text: 'text-green-700',
        accent: 'border-l-4 border-l-green-500',
      },
      FAIL: {
        bg: 'bg-gradient-to-br from-red-50 to-red-100/50',
        border: 'border-red-200',
        text: 'text-red-700',
        accent: 'border-l-4 border-l-red-500',
      },
      PARTIAL: {
        bg: 'bg-gradient-to-br from-yellow-50 to-yellow-100/50',
        border: 'border-yellow-200',
        text: 'text-yellow-700',
        accent: 'border-l-4 border-l-yellow-500',
      },
    }
    return verdictStyles[verdict] || verdictStyles.PARTIAL
  }

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-xl backdrop-blur-[8px]">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="group flex w-full items-center gap-4 px-4 py-3 text-left transition-colors hover:bg-gray-50/50"
          aria-expanded={isExpanded}
        >
          <div className="flex-shrink-0">
            <SiGoogle className="h-5 w-5 text-[#4285f4]" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-gray-900">
              Google Search Console URL Inspection
            </div>
            <div className="text-xs font-medium tracking-wide text-gray-500">
              Index status: {indexStatus} •{' '}
              {issues.length > 0 ? `${issues.length} issues` : 'No issues'}
            </div>
            <div className="truncate text-xs text-gray-600">{url}</div>
          </div>
          <div className="flex items-center gap-2">
            <div
              className={`rounded-full border px-3 py-1 text-xs font-semibold ${statusStyles.badge}`}
            >
              {indexStatus}
            </div>
            <ChevronDown
              className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
              aria-hidden="true"
            />
          </div>
        </button>

        {isExpanded && (
          <div className="border-t border-gray-200/50 px-4 py-4">
            {/* Index Status Overview */}
            <div className="mb-6">
              <div className="mb-4 flex items-center gap-2">
                <div className="text-sm font-bold text-gray-900">Index Status Overview</div>
                <div className="h-px flex-1 bg-gray-200"></div>
              </div>

              {/* Main Status Card */}
              <div
                className={`overflow-hidden rounded-2xl border-2 ${statusStyles.border} ${statusStyles.bg} ${statusStyles.shadow} p-4`}
              >
                <div className="flex items-center gap-3">
                  <div className={`rounded-lg p-2 ${statusStyles.iconBg}`}>
                    <StatusIcon className={`h-6 w-6 ${statusStyles.iconText}`} />
                  </div>
                  <div className="flex-1">
                    <div className="text-lg font-bold text-gray-900">{indexStatus}</div>
                    <div className={`text-sm ${statusStyles.text}`}>
                      {indexStatus === 'INDEXED'
                        ? 'This page is successfully indexed and can appear in search results'
                        : indexStatus === 'NOT_INDEXED'
                          ? 'This page is not indexed and will not appear in search results'
                          : 'Index status is unknown or unclear'}
                    </div>
                  </div>
                  {url && url !== 'Unknown URL' && (
                    <a
                      href={url.startsWith('http') ? url : `https://${url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50"
                    >
                      <ExternalLink className="h-3 w-3" />
                      View Page
                    </a>
                  )}
                </div>
              </div>

              {/* Status Details Grid */}
              {(coverageState || indexingState || lastCrawlTime) && (
                <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3">
                  {coverageState && (
                    <div className="rounded-2xl border border-blue-200 bg-gradient-to-br from-blue-50 to-blue-100/50 p-4 shadow-sm">
                      <div className="mb-2 flex items-center gap-2">
                        <Search className="h-4 w-4 text-blue-600" />
                        <span className="text-xs font-medium text-gray-700">Coverage State</span>
                      </div>
                      <div className="text-sm font-bold text-blue-700">{coverageState}</div>
                    </div>
                  )}
                  {indexingState && (
                    <div className="rounded-2xl border border-purple-200 bg-gradient-to-br from-purple-50 to-purple-100/50 p-4 shadow-sm">
                      <div className="mb-2 flex items-center gap-2">
                        <Shield className="h-4 w-4 text-purple-600" />
                        <span className="text-xs font-medium text-gray-700">Indexing State</span>
                      </div>
                      <div className="text-sm font-bold text-purple-700">{indexingState}</div>
                    </div>
                  )}
                  {lastCrawlTime && (
                    <div className="rounded-2xl border border-orange-200 bg-gradient-to-br from-orange-50 to-orange-100/50 p-4 shadow-sm">
                      <div className="mb-2 flex items-center gap-2">
                        <Clock className="h-4 w-4 text-orange-600" />
                        <span className="text-xs font-medium text-gray-700">Last Crawl</span>
                      </div>
                      <div className="text-xs font-bold text-orange-700">
                        {new Date(lastCrawlTime).toLocaleString()}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Issues Section */}
            {issues.length > 0 && (
              <div className="mb-6">
                <div className="mb-4 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-red-600" />
                    <div className="text-sm font-bold text-gray-900">Issues Found</div>
                  </div>
                  <div className="rounded-full bg-red-100 px-3 py-1 text-xs font-semibold text-red-700">
                    {issues.length} Issue{issues.length !== 1 ? 's' : ''}
                  </div>
                </div>

                <div className="space-y-3">
                  {issues.map((issue, index) => (
                    <div
                      key={index}
                      className={`overflow-hidden rounded-2xl border-2 ${
                        issue.severity === 'ERROR' ? 'border-red-200' : 'border-yellow-200'
                      } ${
                        issue.severity === 'ERROR'
                          ? 'bg-gradient-to-br from-red-50 to-red-100/50'
                          : 'bg-gradient-to-br from-yellow-50 to-yellow-100/50'
                      } shadow-md ${
                        issue.severity === 'ERROR' ? 'shadow-red-100/50' : 'shadow-yellow-100/50'
                      }`}
                    >
                      <div className="p-4">
                        <div className="mb-3 flex items-center gap-2">
                          <div
                            className={`rounded-lg px-3 py-1 text-xs font-bold ${
                              issue.severity === 'ERROR'
                                ? 'bg-red-100 text-red-700'
                                : 'bg-yellow-100 text-yellow-700'
                            }`}
                          >
                            {issue.severity}
                          </div>
                          <div className="text-xs font-medium text-gray-700">{issue.issueType}</div>
                        </div>
                        <div className="text-sm font-medium text-gray-800">{issue.message}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Mobile Usability Section */}
            {mobileUsability && (
              <div className="mb-6">
                <div className="mb-4 flex items-center gap-2">
                  <Smartphone className="h-4 w-4 text-gray-600" />
                  <div className="text-sm font-bold text-gray-900">Mobile Usability</div>
                  <div className="h-px flex-1 bg-gray-200"></div>
                </div>

                <div
                  className={`overflow-hidden rounded-2xl border-2 ${getVerdictStyles(mobileUsability.verdict).border} ${getVerdictStyles(mobileUsability.verdict).bg} ${getVerdictStyles(mobileUsability.verdict).accent} shadow-md`}
                >
                  <div className="p-4">
                    <div className="mb-3 flex items-center gap-3">
                      <div
                        className={`rounded-lg p-2 ${
                          mobileUsability.verdict === 'PASS'
                            ? 'bg-green-100'
                            : mobileUsability.verdict === 'FAIL'
                              ? 'bg-red-100'
                              : 'bg-yellow-100'
                        }`}
                      >
                        {mobileUsability.verdict === 'PASS' ? (
                          <CheckCircle
                            className={`h-5 w-5 ${getVerdictStyles(mobileUsability.verdict).text.replace('text-', 'text-green-')}`}
                          />
                        ) : mobileUsability.verdict === 'FAIL' ? (
                          <XCircle
                            className={`h-5 w-5 ${getVerdictStyles(mobileUsability.verdict).text.replace('text-', 'text-red-')}`}
                          />
                        ) : (
                          <AlertTriangle
                            className={`h-5 w-5 ${getVerdictStyles(mobileUsability.verdict).text.replace('text-', 'text-yellow-')}`}
                          />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="text-lg font-bold text-gray-900">
                          {mobileUsability.verdict}
                        </div>
                        <div
                          className={`text-sm ${getVerdictStyles(mobileUsability.verdict).text}`}
                        >
                          {mobileUsability.verdict === 'PASS'
                            ? 'Page is mobile-friendly and provides a good mobile user experience'
                            : mobileUsability.verdict === 'FAIL'
                              ? 'Page has mobile usability issues that need to be addressed'
                              : 'Mobile usability test completed with some concerns'}
                        </div>
                      </div>
                    </div>

                    {mobileUsability.issues && mobileUsability.issues.length > 0 && (
                      <div className="space-y-2 border-t border-gray-200 pt-3">
                        <div className="text-xs font-semibold text-gray-700">Mobile Issues:</div>
                        {mobileUsability.issues.map((issue, index) => (
                          <div
                            key={index}
                            className="rounded-lg border border-gray-200 bg-white/60 p-2"
                          >
                            <div className="text-xs">
                              <span className="font-medium text-gray-900">{issue.rule}:</span>{' '}
                              <span className="text-gray-700">{issue.severity}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Rich Results Section */}
            {richResults && (
              <div className="mb-6">
                <div className="mb-4 flex items-center gap-2">
                  <Star className="h-4 w-4 text-gray-600" />
                  <div className="text-sm font-bold text-gray-900">Rich Results</div>
                  <div className="h-px flex-1 bg-gray-200"></div>
                </div>

                <div
                  className={`overflow-hidden rounded-2xl border-2 ${getVerdictStyles(richResults.verdict).border} ${getVerdictStyles(richResults.verdict).bg} ${getVerdictStyles(richResults.verdict).accent} shadow-md`}
                >
                  <div className="p-4">
                    <div className="mb-3 flex items-center gap-3">
                      <div
                        className={`rounded-lg p-2 ${
                          richResults.verdict === 'PASS'
                            ? 'bg-green-100'
                            : richResults.verdict === 'FAIL'
                              ? 'bg-red-100'
                              : 'bg-yellow-100'
                        }`}
                      >
                        {richResults.verdict === 'PASS' ? (
                          <Star className={`h-5 w-5 text-green-600`} />
                        ) : richResults.verdict === 'FAIL' ? (
                          <XCircle className={`h-5 w-5 text-red-600`} />
                        ) : (
                          <AlertTriangle className={`h-5 w-5 text-yellow-600`} />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="text-lg font-bold text-gray-900">{richResults.verdict}</div>
                        <div className={`text-sm ${getVerdictStyles(richResults.verdict).text}`}>
                          {richResults.verdict === 'PASS'
                            ? 'Page has valid structured data that can appear as rich results'
                            : richResults.verdict === 'FAIL'
                              ? 'Page has structured data issues preventing rich results'
                              : 'Structured data partially detected with some issues'}
                        </div>
                      </div>
                    </div>

                    {richResults.detectedItems && richResults.detectedItems.length > 0 && (
                      <div className="space-y-3 border-t border-gray-200 pt-3">
                        <div className="text-xs font-semibold text-gray-700">
                          Detected Structured Data:
                        </div>
                        {richResults.detectedItems.map((item, index) => (
                          <div
                            key={index}
                            className="rounded-2xl border border-gray-200 bg-white/80 p-3 shadow-sm"
                          >
                            <div className="mb-2 text-sm font-bold text-blue-700">
                              {item.richResultType}
                            </div>
                            {item.items && item.items.length > 0 && (
                              <div className="space-y-1">
                                {item.items.map((i, idx) => (
                                  <div key={idx} className="flex justify-between text-xs">
                                    <span className="font-medium text-gray-700">{i.name}:</span>
                                    <span className="text-gray-600">{i.value || 'N/A'}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

function downloadGoogleSearchConsoleUrlInspection(_nodeExecution: NodeExecution): Promise<void> {
  return Promise.resolve()
}

function googleSearchConsoleUrlInspectionToMarkdown(nodeExecution: NodeExecution): string {
  const schema = getToolResultSchema(googleSearchConsoleUrlInspection, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return `## Google Search Console URL Inspection\n\nError: Invalid result format`
  }

  const result = parseResult.data

  const getResultData = (): GoogleSearchConsoleUrlInspectionResult => {
    if (Array.isArray(result)) {
      return result[0]
    }
    return result
  }

  const resultData = getResultData()

  const url = nodeExecution.inputs?.url || 'Unknown URL'
  const indexStatus = resultData?.indexStatus || 'UNKNOWN'
  const coverageState = resultData?.coverageState
  const lastCrawlTime = resultData?.lastCrawlTime
  const indexingState = resultData?.indexingState
  const issues = resultData?.issues || []
  const mobileUsability = resultData?.mobileUsability
  const richResults = resultData?.richResults

  const buildMarkdown = (): string => {
    const sections: string[] = [
      `## Google Search Console URL Inspection\n\n`,
      `**URL:** ${url}\n`,
      `**Index Status:** ${indexStatus}\n\n`,
    ]

    if (coverageState) {
      sections.push(`**Coverage State:** ${coverageState}\n`)
    }
    if (indexingState) {
      sections.push(`**Indexing State:** ${indexingState}\n`)
    }
    if (lastCrawlTime) {
      sections.push(`**Last Crawl Time:** ${new Date(lastCrawlTime).toLocaleString()}\n`)
    }
    sections.push(`\n`)

    if (issues.length > 0) {
      sections.push(`### Issues (${issues.length})\n\n`)
      issues.forEach((issue, index) => {
        sections.push(`${index + 1}. **${issue.severity}** - ${issue.issueType}\n`)
        sections.push(`   ${issue.message}\n\n`)
      })
    }

    if (mobileUsability) {
      sections.push(`### Mobile Usability\n\n`)
      sections.push(`**Verdict:** ${mobileUsability.verdict}\n`)
      if (mobileUsability.issues && mobileUsability.issues.length > 0) {
        sections.push(`\n**Issues:**\n`)
        mobileUsability.issues.forEach((issue) => {
          sections.push(`- ${issue.rule}: ${issue.severity}\n`)
        })
      }
      sections.push(`\n`)
    }

    if (richResults) {
      sections.push(`### Rich Results\n\n`)
      sections.push(`**Verdict:** ${richResults.verdict}\n`)
      if (richResults.detectedItems && richResults.detectedItems.length > 0) {
        sections.push(`\n**Detected Items:**\n`)
        richResults.detectedItems.forEach((item) => {
          sections.push(`- ${item.richResultType}\n`)
          if (item.items && item.items.length > 0) {
            item.items.forEach((i) => {
              sections.push(`  - ${i.name}: ${i.value || 'N/A'}\n`)
            })
          }
        })
      }
      sections.push(`\n`)
    }

    sections.push(`---\n`)

    return sections.join('')
  }

  return buildMarkdown()
}

export const GoogleSearchConsoleUrlInspectionDisplay = createWorkflowToolComponent(
  GoogleSearchConsoleUrlInspectionDisplayComponent,
  downloadGoogleSearchConsoleUrlInspection,
  googleSearchConsoleUrlInspectionToMarkdown
)
